import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../../../utils/api';
import Pagination, { getPageSize } from '../../../Pagination';
import PeakToolbar from '../../../PeakToolbar';
import { btnPrimary, card, errBox, note, num, pill, pillAmber, pillGrey, pillRed, sectionHead, table, td, th } from '../../ui';
import { Dialog, Foot, Loader, actBtn, chip, chipOn, emptyLine, field, lbl } from '../../dialog';
import type { FaCategory, TabProps } from './shared';
import { useFaParams } from './shared';
import { acctLabel, pickName } from './AssetActions';

// ── Accounting › Fixed assets › Asset Categories (batch 9 R4 — COA-H1 tab 1, COA-H2, COA-H3) ─────────
// A client's own asset categories, laid out as PEAK's "Asset Category" screen: each row names the
// category, the three accounts its assets post to (the COA mapping), and its default rate and life.
// ⛔ TWO LAYERS, as services/assetCategories.ts keeps them: the decree class is the Royal Decree's tax
// class and only supplies the rate CEILING; the category is the client's own and names the accounts.
// Registering copies the category's accounts and rate onto the asset, so nothing here moves an asset
// already registered — the screen says so under the table.
// Every rule is the server's: the form couples class and nature (the classic component's coupling, so
// the two cannot drift into a pair the server refuses) and otherwise sends what was typed; a refusal
// is shown in the server's own words. The ceiling is SHOWN, never enforced here.
// The form opens full screen (COA-H16) at ?facat=<id>|new — this tab's own param, written through
// useFaParams().set (replace, never ?menu=/?sub=), so a window can be linked to and closing it leaves
// the list, its filter and its page exactly as they were.

type Cls = { code: string; name_th: string; name_en: string; max_rate: number | null; depreciable: number };
type Acct = { id: string; code: string; name_th: string; name_en: string; class: string };
type TF = (key: string, opts?: Record<string, unknown>) => string;
type Show = 'all' | 'active' | 'off';

const NATURES = ['depreciation', 'amortisation', 'none'] as const;
// Each key written in full so a grep finds it; a nature the server adds later prints as sent.
const NATURE_KEY: Record<string, string> = {
  depreciation: 'aw_fac_nature_depreciation', amortisation: 'aw_fac_nature_amortisation', none: 'aw_fac_nature_none',
};
const METHOD_KEY: Record<string, string> = { straight_line: 'aw_fac_method_straight_line' };
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
const naturePill = (n: string): React.CSSProperties => (n === 'none' ? pillGrey : n === 'amortisation' ? pillAmber : pillBlue);
const natureWord = (n: string, t: TF) => (NATURE_KEY[n] ? t(NATURE_KEY[n]) : n);

/** An account as the server labels it ("code · name", services/assetCategories.ts), the code kept on one
 *  line; '—' where the category has none (land has no accumulated and no expense account). */
const acctTd: React.CSSProperties = { ...td, minWidth: 150 };
const AcctLabel = ({ text }: { text: string }) => {
  if (!text) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  const [code, ...rest] = text.split(' · ');
  return <><span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{code}</span>{rest.length ? ` · ${rest.join(' · ')}` : ''}</>;
};
/** "20" / "33.333333" → a number, or NaN. Commas typed as thousands separators are dropped. */
const numIn = (v: string): number => parseFloat(String(v ?? '').replace(/,/g, ''));
const trim = (x: number, dp: number): string => String(Math.round(x * 10 ** dp) / 10 ** dp);
/** ⛔ A life IS a rate (COA-H5, services/fixedAssets.ts lifeFromRate / rateFromLife): life = 100 ÷ rate.
 *  The rate keeps six decimals, as the server's rateFromLife does, so a life typed in comes back as
 *  the same life; the life is shown to two. */
const lifeOf = (rate: number): string => (rate > 0 ? trim(100 / rate, 2) : '');
const rateOf = (life: number): string => (life > 0 ? trim(100 / life, 6) : '');
const ceilingOf = (c: Cls | undefined, t: TF): string =>
  c && Number(c.depreciable) && c.max_rate != null ? t('aw_fac_ceiling', { max: c.max_rate }) : t('aw_fac_class_no_dep');
const isThai = (lang: string) => String(lang || '').startsWith('th');
const ACCT_LABEL = {
  asset: ['asset_account', 'asset_account_en'], accum: ['accum_account', 'accum_account_en'], expense: ['expense_account', 'expense_account_en'],
} as const;
/** A category's account in the reader's language, as the server labels it ("code · name"): the Thai
 *  label on a Thai screen, the English one otherwise (the chart keeps no Chinese name), falling back to
 *  the Thai one where the account has no English name. The table and the form's saved-account option
 *  both read it, so the two never disagree (review b9 #22). */
const acctText = (lang: string, c: FaCategory | null | undefined, k: keyof typeof ACCT_LABEL): string => {
  if (!c) return '';
  const [th, en] = ACCT_LABEL[k];
  return String((isThai(lang) ? c[th] : c[en] || c[th]) || '');
};

/** The tab. Read-only seats and a closed period (canWrite false) get the table only — every write
 *  control is left out, never shown disabled. */
export function CategoriesTab({ ctx, canWrite }: TabProps) {
  const { t, i18n } = useTranslation();
  const lang = String(i18n.language || '');
  const fp = useFaParams();
  const [params] = useSearchParams();
  const formKey = params.get('facat') || '';
  const [cats, setCats] = useState<FaCategory[] | null>(null);
  const [classes, setClasses] = useState<Cls[]>([]);
  const [accounts, setAccounts] = useState<Acct[]>([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState('');
  const [show, setShow] = useState<Show>('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [deleting, setDeleting] = useState<FaCategory | null>(null);

  // `all` = true: a category switched off is still listed here, or it could never be switched back on.
  useEffect(() => {
    let alive = true;
    api.faCategories(ctx.clientId, true)
      .then((d: any) => { if (alive) setCats(d?.categories || []); })
      .catch((e: any) => { if (alive) { setCats([]); setErr(String(e?.message || e)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  // The form's choices: the decree classes, and the client's postable accounts. A read-only seat never
  // sees the form, so it never asks for them.
  useEffect(() => {
    if (!canWrite) return;
    let alive = true;
    api.faClasses().then((d: any) => { if (alive) setClasses(d?.classes || []); }).catch(() => { /* the form shows no classes */ });
    api.jnlAccounts(ctx.clientId).then((d: any) => { if (alive) setAccounts(d?.accounts || []); }).catch(() => { /* the form shows no accounts */ });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, canWrite]);

  const list = cats || [];
  const shown = list.filter(c => (show === 'all' ? true : show === 'active' ? !!Number(c.active) : !Number(c.active)));
  const pages = Math.max(1, Math.ceil(shown.length / limit));
  const pg = Math.min(page, pages);
  const rows = shown.slice((pg - 1) * limit, pg * limit);

  const formCat = formKey && formKey !== 'new' ? list.find(c => c.id === formKey) || null : null;
  const formOpen = canWrite && cats !== null && (formKey === 'new' || !!formCat);
  const openForm = (key: string) => { setErr(''); setMsg(''); fp.set({ facat: key }); };
  const closeForm = () => fp.set({ facat: '' });
  // A link to a category this client does not have (deleted since, or mistyped) opens nothing and is
  // dropped from the address, so the next Edit is not shadowed by it.
  const stale = canWrite && cats !== null && !!formKey && formKey !== 'new' && !formCat;
  useEffect(() => { if (stale) fp.set({ facat: '' }); }, [stale]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ⛔ After every write: the shell's reload counter, which re-reads this list (and anything else
  // mounted later) without resetting the filter or the page.
  const toggle = async (c: FaCategory) => {
    const on = !!Number(c.active);
    setBusyId(c.id); setErr(''); setMsg('');
    try {
      await api.faUpdateCategory(c.id, { active: !on });
      setMsg(on ? t('aw_fac_switched_off', { name: c.name_th }) : t('aw_fac_switched_on', { name: c.name_th }));
      ctx.bump();
    } catch (e: any) { setErr(String(e?.message || e)); } finally { setBusyId(''); }
  };

  const addBtn = canWrite ? (
    <button type="button" onClick={() => openForm('new')} data-fac-add style={{ ...btnPrimary, flex: 'none' }}>
      <i className="ti ti-plus" aria-hidden /> {t('aw_fac_add')}
    </button>
  ) : undefined;
  const filters = (
    <>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }} data-fac-count>{t('aw_fac_count', { n: list.length })}</span>
      <span style={{ width: 1, height: 22, background: 'var(--border-strong)', flex: 'none' }} aria-hidden />
      <div role="group" aria-label={t('aw_fac_show_label')} style={{ display: 'flex', gap: 6 }}>
        {(['all', 'active', 'off'] as Show[]).map(s => (
          <button key={s} type="button" aria-pressed={s === show} data-fac-show={s} onClick={() => { setShow(s); setPage(1); }}
            style={{ ...(s === show ? { ...chip, ...chipOn } : chip), flex: 'none', whiteSpace: 'nowrap' }}>
            {s === 'all' ? t('aw_fac_show_all') : s === 'active' ? t('aw_fac_show_active') : t('aw_fac_show_off')}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-fa-categories>
      {msg && <div role="status" style={{ ...card, padding: '8px 12px', fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderColor: '#BBE5CB' }}>{msg}</div>}
      {err && <div role="alert" style={errBox}>{err}</div>}
      {cats === null ? <Loader /> : !list.length ? (
        // COA-H3: every asset needs a category, so an empty list says why it matters and where to start.
        <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-start' }} data-fac-empty>
          <i className="ti ti-category" style={{ fontSize: 22, color: 'var(--accent)', flex: 'none' }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('aw_fac_empty_title')}</div>
            <div style={{ ...note, fontSize: 12.5, marginTop: 4 }}>{t('aw_fac_empty_body')}</div>
            <div style={{ ...note, marginTop: 4 }}>{t('aw_fac_intro')}</div>
            {addBtn && <div style={{ marginTop: 10 }}>{addBtn}</div>}
          </div>
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'visible' }}>
          <PeakToolbar filters={filters} right={addBtn} />
          <div style={{ overflowX: 'auto' }}>
            {!rows.length ? <div style={emptyLine}>{t('aw_fac_none_shown')}</div> : (
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>{t('aw_fac_col_category')}</th>
                    <th style={th}>{t('aw_fac_col_asset')}</th>
                    <th style={th}>{t('aw_fac_col_expense')}</th>
                    <th style={th}>{t('aw_fac_col_accum')}</th>
                    <th style={th}>{t('aw_fac_col_rate')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_fac_col_assets')}</th>
                    {canWrite && <th style={th}>{t('aw_fac_col_actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(c => {
                    const never = c.nature === 'none';
                    const on = !!Number(c.active);
                    const rate = Number(c.default_rate);
                    // ⛔ An asset was registered under it (disposed and removed ones count): the server refuses
                    // the delete, and a refusal pages the admins — so Delete is not drawn at all (review b9 #27).
                    const inUse = Number(c.assets) > 0;
                    const clsName = pickName(lang, c.class_th, c.class_en);
                    const clsOther = isThai(lang) ? c.class_en : c.class_th;
                    return (
                      <tr key={c.id} data-cat-row={c.id} style={{ opacity: on ? 1 : 0.6 }}>
                        <td style={td}>
                          <div style={{ fontWeight: 700 }} data-cat-name>{c.name_th}</div>
                          {c.name_en && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.name_en}</div>}
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4, alignItems: 'center' }}>
                            <span style={naturePill(c.nature)} data-cat-nature={c.nature}>{natureWord(c.nature, t)}</span>
                            {!on && <span style={pillRed} data-cat-inactive>{t('aw_fac_inactive')}</span>}
                            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }} title={clsOther && clsOther !== clsName ? clsOther : undefined} data-cat-class>
                              {clsName || c.decree_class_code}{c.max_rate != null && Number(c.depreciable) ? ` · ${t('aw_fac_ceiling', { max: c.max_rate })}` : ''}
                            </span>
                          </div>
                        </td>
                        <td style={acctTd} data-cat-cell="asset"><AcctLabel text={acctText(lang, c, 'asset')} /></td>
                        <td style={acctTd} data-cat-cell="expense"><AcctLabel text={never ? '' : acctText(lang, c, 'expense')} /></td>
                        <td style={acctTd} data-cat-cell="accum"><AcctLabel text={never ? '' : acctText(lang, c, 'accum')} /></td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} data-cat-cell="rate">
                          {never || !(rate > 0) ? '—' : t('aw_fac_rate_life', { rate: trim(rate, 2), life: lifeOf(rate) })}
                        </td>
                        <td style={num} title={t('aw_fac_assets_title')} data-cat-cell="assets">{Number(c.assets) || 0}</td>
                        {canWrite && (
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', gap: 5 }}>
                              <button type="button" onClick={() => openForm(c.id)} data-cat-edit={c.id} style={actBtn}>
                                <i className="ti ti-pencil" aria-hidden /> {t('aw_fac_edit')}
                              </button>
                              <button type="button" onClick={() => toggle(c)} disabled={!!busyId} data-cat-toggle={c.id} style={actBtn}
                                title={inUse ? t('aw_fac_in_use_hint', { n: Number(c.assets) }) : undefined}>
                                <i className={busyId === c.id ? 'ti ti-loader' : on ? 'ti ti-toggle-right' : 'ti ti-toggle-left'} aria-hidden /> {on ? t('aw_fac_off') : t('aw_fac_on')}
                              </button>
                              {!inUse && (
                                <button type="button" onClick={() => { setErr(''); setMsg(''); setDeleting(c); }} disabled={!!busyId} data-cat-delete={c.id}
                                  style={{ ...actBtn, color: '#B42318' }}>
                                  <i className="ti ti-trash" aria-hidden /> {t('aw_fac_delete')}
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {cats !== null && <Pagination page={pg} total={shown.length} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      {!!list.length && <div style={note}>{t('aw_fac_snapshot_note')}</div>}
      {formOpen && (
        <CategoryForm key={formKey} clientId={ctx.clientId} cat={formCat} classes={classes} accounts={accounts}
          onClose={closeForm} onSaved={() => { closeForm(); setMsg(t('aw_fac_saved')); ctx.bump(); }} />
      )}
      {deleting && canWrite && (
        <DeleteDialog cat={deleting} onClose={() => setDeleting(null)} onRefused={() => ctx.bump()}
          onDeleted={() => { setMsg(t('aw_fac_deleted', { name: deleting.name_th })); setDeleting(null); ctx.bump(); }} />
      )}
    </div>
  );
}

const Field = ({ label, children, span, hint }: { label: React.ReactNode; children: React.ReactNode; span?: number; hint?: React.ReactNode }) => (
  <label style={{ display: 'block', gridColumn: span ? `span ${span}` : undefined, minWidth: 0 }}>
    <span style={lbl}>{label}</span>
    {children}
    {hint ? <span style={{ ...note, display: 'block', marginTop: 4 }}>{hint}</span> : null}
  </label>
);

/**
 * Create or edit one category, full screen (COA-H16). The classic component's form (Bookkeeping/
 * FixedAssets.tsx) rebuilt as a window: the same class ↔ nature coupling, the same body, and the
 * server's refusal shown as it was sent. `dirty` is set by the edit path only — opening an existing
 * category fills the form without marking it.
 */
function CategoryForm({ clientId, cat, classes, accounts, onClose, onSaved }: {
  clientId: string; cat: FaCategory | null; classes: Cls[]; accounts: Acct[]; onClose: () => void; onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = String(i18n.language || '');
  const r0 = cat && cat.default_rate != null && Number(cat.default_rate) > 0 ? Number(cat.default_rate) : 0;
  const [classCode, setClassCode] = useState(cat?.decree_class_code || '');
  const [nature, setNature] = useState<string>(cat?.nature || 'depreciation');
  const [nameTh, setNameTh] = useState(cat?.name_th || '');
  const [nameEn, setNameEn] = useState(cat?.name_en || '');
  const [rate, setRate] = useState(r0 ? trim(r0, 6) : '');
  const [life, setLife] = useState(r0 ? lifeOf(r0) : '');
  const [method, setMethod] = useState(cat?.default_method || 'straight_line');
  const [assetId, setAssetId] = useState(cat?.asset_account_id || '');
  const [accumId, setAccumId] = useState(cat?.accum_account_id || '');
  const [expenseId, setExpenseId] = useState(cat?.expense_account_id || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };

  // ⛔ The decree class and the kind of charge are ONE choice made two ways (the classic coupling):
  // a class the decree does not depreciate makes the category one that is never charged, and "never
  // charged" moves it onto such a class. The server refuses the two apart; a form that let them drift
  // would only collect that refusal. Leaving "never charged" clears the class, so one is CHOSEN.
  const neverCharged = (code: string) => { const c = classes.find(x => x.code === code); return !!c && !Number(c.depreciable); };
  const firstNever = () => String(classes.find(c => !Number(c.depreciable))?.code || '');
  const never = nature === 'none';
  const cls = classes.find(c => c.code === classCode);
  const onClass = (v: string) => {
    ed(setClassCode, v);
    if (neverCharged(v)) { setNature('none'); setRate(''); setLife(''); }
    else if (nature === 'none') setNature('depreciation');
  };
  const onNature = (v: string) => {
    ed(setNature, v);
    if (v === 'none') { if (!neverCharged(classCode)) setClassCode(firstNever()); setRate(''); setLife(''); }
    else if (neverCharged(classCode)) setClassCode('');
  };
  // The two linked inputs: typing one fills the other.
  const onRate = (v: string) => { ed(setRate, v); const n = numIn(v); setLife(n > 0 ? lifeOf(n) : ''); };
  const onLife = (v: string) => { ed(setLife, v); const n = numIn(v); setRate(n > 0 ? rateOf(n) : ''); };

  const ready = !!nameTh.trim() && !busy;
  const submit = async () => {
    if (!ready) return;
    setBusy(true); setErr('');
    const r = numIn(rate);
    const body = {
      nameTh: nameTh.trim(), nameEn: nameEn.trim(), decreeClassCode: classCode, nature, defaultMethod: method,
      // Land carries no rate and no two accounts at all — null and '', never a hidden leftover.
      defaultRate: never || !rate.trim() || !Number.isFinite(r) ? null : r,
      assetAccountId: assetId,
      accumAccountId: never ? '' : accumId,
      expenseAccountId: never ? '' : expenseId,
    };
    try {
      if (cat) await api.faUpdateCategory(cat.id, body);
      else await api.faCreateCategory(clientId, body);
      onSaved();
    } catch (e: any) { setErr(String(e?.message || e)); setBusy(false); }
  };

  /** One account picker. The list is the client's postable accounts of the class the slot takes, each
   *  read "code · name" in the reader's language (acctLabel, as the register's own form reads them); an
   *  account the category already names that is no longer offered (switched off since) stays visible
   *  as the server labels it — in the reader's language too — so opening the form never silently
   *  changes it. */
  const acctSelect = (k: 'assetAccountId' | 'accumAccountId' | 'expenseAccountId', value: string, set: (v: string) => void, cl: string, saved: string) => {
    const opts = accounts.filter(a => a.class === cl);
    return (
      <select value={value} onChange={e => ed(set, e.target.value)} data-fac-field={k} style={field}>
        <option value="">{t('aw_fac_pick')}</option>
        {value && !opts.some(a => a.id === value) && <option value={value}>{saved || value}</option>}
        {opts.map(a => <option key={a.id} value={a.id}>{acctLabel(lang, a)}</option>)}
      </select>
    );
  };
  const req = <span title={t('aw_fac_required')} style={{ color: '#B42318', textTransform: 'none', fontWeight: 600 }}> *</span>;

  return (
    <Dialog fullscreen title={cat ? t('aw_fac_edit_title') : t('aw_fac_new_title')} docNo={cat ? cat.name_th : undefined}
      onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={cat ? 'ti ti-device-floppy' : 'ti ti-plus'}
        label={cat ? t('aw_fac_save_btn') : t('aw_fac_create_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 920 }} data-fac-form={cat ? cat.id : 'new'}>
        {err && <div role="alert" style={errBox} data-fac-error>{err}</div>}
        <div style={note}>{t('aw_fac_intro')}</div>

        <section>
          <div style={sectionHead}>{t('aw_fac_sec_class')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <Field label={t('aw_fac_f_class')}>
              <select value={classCode} onChange={e => onClass(e.target.value)} data-fac-field="decreeClassCode" style={field}>
                <option value="">{t('aw_fac_pick')}</option>
                {classes.map(c => <option key={c.code} value={c.code}>{pickName(lang, c.name_th, c.name_en) || c.code} ({ceilingOf(c, t)})</option>)}
              </select>
            </Field>
            <Field label={t('aw_fac_f_nature')}>
              <select value={nature} onChange={e => onNature(e.target.value)} data-fac-field="nature" style={field}>
                {NATURES.map(n => <option key={n} value={n}>{natureWord(n, t)}</option>)}
              </select>
            </Field>
          </div>
        </section>

        <section>
          <div style={sectionHead}>{t('aw_fac_sec_names')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <Field label={<>{t('aw_fac_f_name_th')}{req}</>}>
              <input value={nameTh} onChange={e => ed(setNameTh, e.target.value)} maxLength={200} required aria-required data-fac-field="nameTh" style={field} />
            </Field>
            <Field label={t('aw_fac_f_name_en')}>
              <input value={nameEn} onChange={e => ed(setNameEn, e.target.value)} maxLength={200} data-fac-field="nameEn" style={field} />
            </Field>
          </div>
        </section>

        {!never && (
          <section>
            <div style={sectionHead}>{t('aw_fac_sec_defaults')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <Field label={<>{t('aw_fac_f_rate')}{cls && Number(cls.depreciable) && cls.max_rate != null
                ? <span style={{ color: 'var(--accent-text)', textTransform: 'none', marginLeft: 6 }} data-fac-ceiling>{t('aw_fac_ceiling', { max: cls.max_rate })}</span> : null}</>}>
                <input value={rate} onChange={e => onRate(e.target.value)} inputMode="decimal" data-fac-field="defaultRate"
                  placeholder={cls?.max_rate != null ? t('aw_fac_ceiling', { max: cls.max_rate }) : ''} style={{ ...field, textAlign: 'right' }} />
              </Field>
              <Field label={t('aw_fac_f_life')}>
                <input value={life} onChange={e => onLife(e.target.value)} inputMode="decimal" data-fac-field="usefulLife" style={{ ...field, textAlign: 'right' }} />
              </Field>
              <Field label={t('aw_fac_f_method')}>
                <select value={method} onChange={e => ed(setMethod, e.target.value)} data-fac-field="defaultMethod" style={field}>
                  {Object.keys(METHOD_KEY).map(m => <option key={m} value={m}>{t(METHOD_KEY[m])}</option>)}
                  {!METHOD_KEY[method] && <option value={method}>{method}</option>}
                </select>
              </Field>
            </div>
            <div style={{ ...note, marginTop: 6 }}>
              {t('aw_fac_linked_note')}
              {cls && Number(cls.depreciable) && cls.max_rate != null ? <> {t('aw_fac_ceiling_note', { max: cls.max_rate })}</> : null}
            </div>
          </section>
        )}

        <section>
          <div style={sectionHead}>{t('aw_fac_sec_accounts')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <Field label={t('aw_fac_f_asset')}>{acctSelect('assetAccountId', assetId, setAssetId, 'asset', acctText(lang, cat, 'asset'))}</Field>
            {/* ⛔ Not shown, and not sent, for a category that is never charged: land has nothing to
                accumulate and nothing to charge, so there is no account for either. */}
            {!never && <Field label={t('aw_fac_f_accum')}>{acctSelect('accumAccountId', accumId, setAccumId, 'asset', acctText(lang, cat, 'accum'))}</Field>}
            {!never && <Field label={t('aw_fac_f_expense')}>{acctSelect('expenseAccountId', expenseId, setExpenseId, 'expense', acctText(lang, cat, 'expense'))}</Field>}
          </div>
          {never && <div style={{ ...note, marginTop: 6 }} data-fac-never>{t('aw_fac_never_note')}</div>}
        </section>

        <div style={note}>{t('aw_fac_snapshot_note')}</div>
      </div>
    </Dialog>
  );
}

/** Delete, asked first in a small window. ⛔ The server refuses while any asset — disposed ones
 *  included — was ever registered under the category. The table offers Delete only on a category it
 *  shows with no asset, so a refusal here means one was registered since the list was read: it is
 *  shown in the server's own words, the window stays open so it can be read, and the list re-reads
 *  (onRefused) so the row then shows its asset and no Delete. */
function DeleteDialog({ cat, onClose, onDeleted, onRefused }: { cat: FaCategory; onClose: () => void; onDeleted: () => void; onRefused: () => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setBusy(true); setErr('');
    try { await api.faDeleteCategory(cat.id); onDeleted(); }
    catch (e: any) { setErr(String(e?.message || e)); setBusy(false); onRefused(); }
  };
  return (
    <Dialog title={t('aw_fac_delete_title')} docNo={cat.name_th} onClose={onClose} busy={busy}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={!err} icon="ti ti-trash" label={t('aw_fac_delete_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-fac-delete={cat.id}>
        <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{t('aw_fac_delete_confirm', { name: cat.name_th })}</div>
        {err && <div role="alert" style={errBox} data-fac-error>{err}</div>}
      </div>
    </Dialog>
  );
}
